// ============================================================================
// TABLE SOUND EFFECTS
// ----------------------------------------------------------------------------
// One place that decides how table sounds behave, so a sound can never
// stack on itself, fire twice for the same event, or run on for seconds
// after the moment it was for. Both the cash/tournament table and the
// training table play through here.
//
// Rules, per sound:
//   minGapMs  - the same sound will not start again within this window
//               (two effects reacting to one game-state change, or three
//               callers all announcing the same new hand, play it once)
//   solo      - never restart while a copy is still playing (the long win
//               jingle, the jackpot)
//   maxMs     - fade out and stop after this long, for files that are
//               longer than the moment they mark (the 2.5s deal shuffle
//               is a hand-start sound, not a street sound)
// ============================================================================

export interface SfxSpec {
  file: string;
  volume: number;
  minGapMs?: number;
  solo?: boolean;
  maxMs?: number;
}

export const TABLE_SFX: Record<string, SfxSpec> = {
  // A new hand: the shuffle-and-deal sequence, trimmed so it ends as the
  // cards land instead of running under the first decision.
  'hand-start': { file: 'card-deal.mp3', volume: 0.45, minGapMs: 2500, maxMs: 1400 },
  // Flop, turn, river: one short card drop per street, not a shuffle.
  'card-deal': { file: 'carddrop2-92718.mp3', volume: 0.5, minGapMs: 250 },
  'card-drop': { file: 'carddrop2-92718.mp3', volume: 0.5, minGapMs: 120 },
  // Your turn: a short cue, once per turn.
  'your-turn': { file: 'your-turn.mp3', volume: 0.45, minGapMs: 2500, maxMs: 1100 },

  // Actions
  'fold': { file: 'folding-37422.mp3', volume: 0.5, minGapMs: 120 },
  'check': { file: 'button-click.mp3', volume: 0.5, minGapMs: 120 },
  'call': { file: 'poker_chips1-87592.mp3', volume: 0.5, minGapMs: 120 },
  'chip-single': { file: 'poker_chips1-87592.mp3', volume: 0.5, minGapMs: 120 },
  'bet': { file: 'poker_chips1-87592.mp3', volume: 0.5, minGapMs: 120 },
  'raise': { file: 'poker_chips1-87592.mp3', volume: 0.5, minGapMs: 120 },
  'all-in': { file: 'allinpushchips-96121.mp3', volume: 0.7, minGapMs: 300 },

  // Results. The win jingle is long: it plays once, never on top of itself,
  // and only for the player who actually won. Losing gets a quiet, short cue.
  'win': { file: 'you-win-sequence-2-183949.mp3', volume: 0.55, solo: true, minGapMs: 3000 },
  'lose': { file: 'marimba-lose-250960.mp3', volume: 0.25, minGapMs: 3000, maxMs: 1800 },
  'error': { file: 'marimba-lose-250960.mp3', volume: 0.35, minGapMs: 800, maxMs: 1200 },
  'jackpot': { file: 'jackpot.mp3', volume: 0.7, solo: true, minGapMs: 3000 },

  // UI
  'button-click': { file: 'button-click.mp3', volume: 0.3, minGapMs: 80 },
};

const live = new Map<string, HTMLAudioElement>();
const lastStart = new Map<string, number>();
const fadeTimers = new Map<string, number>();

function stopKey(key: string): void {
  const a = live.get(key);
  if (a) {
    try { a.pause(); } catch { /* ignore */ }
    live.delete(key);
  }
  const t = fadeTimers.get(key);
  if (t) { window.clearInterval(t); fadeTimers.delete(key); }
}

/**
 * Play a table sound by key, honouring the per-sound rules above. Returns
 * false when the sound was skipped (disabled, unknown, throttled or solo).
 */
export function playTableSfx(key: string, enabled: boolean = true): boolean {
  if (!enabled) return false;
  const spec = TABLE_SFX[key];
  if (!spec) return false;

  const now = Date.now();
  const prev = lastStart.get(key) || 0;
  if (spec.minGapMs && now - prev < spec.minGapMs) return false;
  const playing = live.get(key);
  if (spec.solo && playing && !playing.paused && !playing.ended) return false;

  try {
    // One element per key: a re-trigger restarts the same clip instead of
    // layering a fresh copy over the one still playing.
    stopKey(key);
    const audio = new Audio(`/sounds/${spec.file}`);
    audio.volume = spec.volume;
    audio.setAttribute('playsinline', 'true');
    live.set(key, audio);
    lastStart.set(key, now);
    audio.addEventListener('ended', () => { if (live.get(key) === audio) live.delete(key); });
    const p = audio.play();
    if (p && typeof p.catch === 'function') p.catch(() => { if (live.get(key) === audio) live.delete(key); });

    if (spec.maxMs) {
      const fadeAt = now + spec.maxMs;
      const timer = window.setInterval(() => {
        if (live.get(key) !== audio) { window.clearInterval(timer); fadeTimers.delete(key); return; }
        const t = Date.now();
        if (t < fadeAt) return;
        const next = Math.max(0, audio.volume - spec.volume / 5);
        audio.volume = next;
        if (next <= 0.001) { stopKey(key); }
      }, 50);
      fadeTimers.set(key, timer);
    }
    return true;
  } catch {
    return false;
  }
}

/** Stop everything (leaving the table, unmounting). */
export function stopAllTableSfx(): void {
  for (const key of Array.from(live.keys())) stopKey(key);
}

/**
 * The end-of-hand cue for one viewer: the win jingle only when they won,
 * a quiet lose cue when they were in the hand and lost, nothing when they
 * had folded or were not dealt in.
 */
export function playHandResultSfx(
  winners: Array<{ playerId?: string; id?: string }> | undefined | null,
  players: Array<{ id: string; folded?: boolean; eliminated?: boolean }> | undefined | null,
  uid: string,
  enabled: boolean = true,
): void {
  if (!enabled || !uid) return;
  const won = (winners || []).some(w => (w.playerId || w.id) === uid);
  if (won) { playTableSfx('win', true); return; }
  const me = (players || []).find(p => p && p.id === uid);
  if (me && !me.folded && !me.eliminated) playTableSfx('lose', true);
}
